import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import AssetDetailSection from '../../../assets/components/AssetDetailSection';
import { paths } from '../../../../routes';
import { useGetProductsQuery } from '../../../../api/group/groupAdminApiSlice';
import { useTenantCapabilities } from '../../../../hooks/useTenantCapabilities';
import CustomButton from '../../../../components/CustomButton';
import {
    type StationCreationForm,
    type StationProductRow,
    bookableObjectCount,
    productRowsTotal,
} from '../stationCreationForm';
import {
    productsByUseCase,
    resolvableUseCases,
    stationLinkableProducts,
} from '../productUseCases';
import NativeSelect from '../../../../components/ui/NativeSelect';

interface LinkProductStepProps {
    form: StationCreationForm;
    update: <K extends keyof StationCreationForm>(key: K, value: StationCreationForm[K]) => void;
}

/**
 * Step 4 — link the objects to the Product they fulfil (`product_resource_link`,
 * architecture §3). Always `any_of`: a station's objects are an interchangeable
 * pool. `exclusive` (the whole set booked as one unit) is a second Product over
 * the same objects, added later — not a choice made while creating them.
 *
 * The Product is **required**, not optional: objects created without one are
 * configured and unbookable, which is the outcome the interim `Group` exists to
 * prevent. A tenant with no Product creates one in Services first — the link
 * below goes there.
 *
 * Reads the real product list (GET /admin/product): unlike the admin group
 * list it also carries products created in Services that have no location yet
 * — those get their geography from this very station when the objects are
 * linked (location adoption, lib/inventory/interimGroup.ts). Offered are only
 * products of the tenant's ACTIVE use cases (a use case switched off in
 * Account must not resurface through its products here), and never
 * venue/apartment products. When the capability config is unresolved the
 * service filter stands down rather than locking the wizard (display-level
 * fallback, see useTenantCapabilities) — the station-family exclusion holds
 * either way.
 */
const LinkProductStep = ({ form, update }: LinkProductStepProps) => {
    const { t } = useTranslation();
    const count = bookableObjectCount(form) ?? 0;
    const { data, isLoading, isError, refetch } = useGetProductsQuery();

    /**
     * Pick up a Product created in the other tab.
     *
     * "Produkt in Services anlegen" opens a second tab with its own Redux
     * store, so `createProduct`'s cache invalidation never reaches this one and
     * the new Product simply never appears here. Nothing in admin-ui calls
     * `setupListeners`, so there is no refetch-on-focus anywhere — this step
     * asks for itself when the operator comes back to the tab, which is exactly
     * the moment the answer has changed.
     */
    useEffect(() => {
        const onFocus = () => { refetch(); };
        window.addEventListener('focus', onFocus);
        return () => window.removeEventListener('focus', onFocus);
    }, [refetch]);
    const { services } = useTenantCapabilities();

    // A Product whose use case does not resolve cannot be offered anywhere: the
    // location's service scope is derived from it, and an unresolvable one is
    // refused by the backend at the very last step of the wizard. The rule lives
    // in `productUseCases` because the add-objects dialog asks it too.
    const products = useMemo(
        () => stationLinkableProducts(data ?? [], services),
        [data, services],
    );

    /** Use-case label, from the same keys the Services area uses. */
    const labelForUseCase = (useCase: string): string =>
        t(`service_type_selector.${useCase.toLowerCase()}`, { defaultValue: useCase });

    /** Products by use case, so the picker can group them. */
    const groupedProducts = useMemo(() => productsByUseCase(products), [products]);

    /*
     * The use cases the rows bring in, in the order they were picked.
     *
     * Plural because a station may hold several Products (#2280) and the scope
     * of a NEW location is the union of theirs — showing only the first one's
     * would state a consequence that is not the one about to happen.
     */
    const selectedUseCases = useMemo(() => {
        const chosen = form.productRows
            .map((row) => products.find((p) => p.id === row.productId.trim()))
            .map((product) => (product ? resolvableUseCases(product)[0] ?? '' : ''))
            .filter((useCase) => useCase.length > 0);
        return Array.from(new Set(chosen));
    }, [products, form.productRows]);

    const rows = form.productRows;
    const total = productRowsTotal(form);

    const updateRow = (index: number, patch: Partial<StationProductRow>) => {
        update('productRows', rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
    };

    const addRow = () => {
        /*
         * Adding the SECOND row is the moment counts start to matter — until
         * then one row means "all of them". So the first row's count is seeded
         * with what it implicitly had, rather than left empty for the operator
         * to work out: without this, adding a row turns a valid step into an
         * invalid one with two blank numbers and no hint of what they sum to.
         */
        const seeded = rows.length === 1 && !rows[0]?.count.trim()
            ? [{ ...rows[0] as StationProductRow, count: String(count) }]
            : rows;
        update('productRows', [...seeded, { productId: '', count: '' }]);
    };

    const removeRow = (index: number) => {
        const remaining = rows.filter((_, i) => i !== index);
        // Back to a single row means back to "all of them", so its count stops
        // being a number the operator has to maintain.
        update('productRows', remaining.length === 1
            ? [{ ...remaining[0] as StationProductRow, count: '' }]
            : remaining);
    };

    return (
        <AssetDetailSection
            title={t('station_create.link.title')}
            subtitle={t('station_create.link.subtitle')}
        >
            <div className={'flex flex-col gap-5'}>
                <div className={'flex flex-col gap-1'}>
                    <label
                        htmlFor={'station-create-product'}
                        className={'text-sm font-medium text-foreground/60'}
                    >
                        {t('station_create.link.product_label')}
                    </label>

                    {isLoading && (
                        <p className={'text-sm text-foreground/60'}>{t('common.loading')}</p>
                    )}

                    {!isLoading && isError && (
                        <p className={'text-sm text-foreground'}>
                            {t('station_create.link.products_error')}
                        </p>
                    )}

                    {!isLoading && !isError && products.length === 0 && (
                        <p className={'text-sm text-foreground'}>
                            {t('station_create.link.no_products')}
                        </p>
                    )}

                    {!isLoading && !isError && products.length > 0 && (
                        <div className={'flex flex-col gap-3'}>
                            {rows.map((row, index) => (
                                <div
                                    // Index as key: the rows have no id of their
                                    // own, and the list is only ever appended to
                                    // or spliced — never reordered.
                                    key={`product-row-${index}`}
                                    className={'flex flex-wrap items-end gap-3'}
                                >
                                    <div className={'flex min-w-[16rem] flex-1 flex-col gap-1'}>
                                        <NativeSelect
                                            id={index === 0
                                                ? 'station-create-product'
                                                : `station-create-product-${index}`}
                                            // Only the first select is covered
                                            // by the step's visible label; the
                                            // rest would otherwise be unnamed.
                                            {...(index === 0 ? {} : {
                                                'aria-label': t(
                                                    'station_create.link.row_product_label',
                                                    { row: index + 1 },
                                                ),
                                            })}
                                            value={row.productId}
                                            onChange={(e) =>
                                                updateRow(index, { productId: e.target.value })}
                                        >
                                            <option value={''}>
                                                {t('station_create.link.product_placeholder')}
                                            </option>
                                            {/* Grouped by use case, because the use case is the
                                                consequence of this choice and was invisible in a
                                                flat list: the NEW location's service scope is
                                                derived from the Product, so picking a rental
                                                product for a locker bank scopes the location
                                                `['rental']` and it never appears in the storage
                                                flow — with no error on any screen (QA #2439,
                                                defect I). Names alone ("L", "M", "Pick-up only")
                                                carry nothing to decide on. */}
                                            {groupedProducts.map(([useCase, items]) => (
                                                <optgroup
                                                    key={useCase}
                                                    label={labelForUseCase(useCase)}
                                                >
                                                    {items.map((product) => (
                                                        <option key={product.id} value={product.id}>
                                                            {product.name ?? product.id}
                                                        </option>
                                                    ))}
                                                </optgroup>
                                            ))}
                                        </NativeSelect>
                                    </div>

                                    {/* The count appears only from the second row
                                        on. With one Product it is the position
                                        count from step 2, and asking for a number
                                        the operator already gave is how a form
                                        earns two sources of truth. */}
                                    {rows.length > 1 && (
                                        <div className={'flex w-28 flex-col gap-1'}>
                                            <label
                                                htmlFor={`station-create-product-count-${index}`}
                                                className={'text-xs text-foreground/60'}
                                            >
                                                {t('station_create.link.row_count_label')}
                                            </label>
                                            <input
                                                id={`station-create-product-count-${index}`}
                                                type={'text'}
                                                inputMode={'numeric'}
                                                value={row.count}
                                                onChange={(e) =>
                                                    updateRow(index, { count: e.target.value })}
                                                className={'h-10 rounded-[5px] border border-foreground/15 bg-text-contrast px-3 text-sm text-foreground'}
                                            />
                                        </div>
                                    )}

                                    {rows.length > 1 && (
                                        <CustomButton
                                            title={t('station_create.link.row_remove')}
                                            isFilled={false}
                                            onClick={() => removeRow(index)}
                                        />
                                    )}
                                </div>
                            ))}

                            <div>
                                <CustomButton
                                    title={t('station_create.link.row_add')}
                                    isFilled={false}
                                    onClick={addRow}
                                />
                            </div>

                            {/* Said before the second row exists, not after:
                                "one station, one product" was the belief the
                                wizard taught, and a control nobody knows to
                                press is the same as a missing one. */}
                            <p className={'text-sm text-foreground/60'}>
                                {t('station_create.link.rows_hint')}
                            </p>

                            {/* What the rows add up to, against what the station
                                has. A mismatch is refused by the step, but the
                                operator has to be able to SEE the gap while
                                typing rather than only be told at the end. */}
                            {rows.length > 1 && (
                                <p className={'text-sm text-foreground/60'}>
                                    {t('station_create.link.row_total', {
                                        assigned: total,
                                        positions: count,
                                    })}
                                </p>
                            )}
                        </div>
                    )}

                    {/* Say what the choice just did. The scope is not a detail
                        of the Product — it decides which offer this station
                        appears under for customers. */}
                    {selectedUseCases.length > 0 && (
                        <p className={'mt-1 text-sm text-foreground/60'}>
                            {t('station_create.link.use_case_note', {
                                useCase: selectedUseCases.map(labelForUseCase).join(', '),
                            })}
                        </p>
                    )}
                </div>

                {/* No mode toggle. `exclusive` — "all N objects are booked as
                    one unit" — is a PRODUCT decision, not a station-creation
                    one: booking the whole station and booking a single Fach are
                    two Products over the same objects (architecture §2d/§3),
                    not a switch here. Asking it at creation time produced a
                    choice nobody could interpret, least of all for storage
                    (PO, staging 2026-08-31). A station's objects are always a
                    pool; the whole-unit Product is added afterwards through
                    `POST /product-links`. */}
                <p className={'text-sm text-foreground/60'}>
                    {t('station_create.link.any_of_hint', { count })}
                </p>
                <p className={'text-sm text-foreground/60'}>
                    {t('station_create.link.create_in_services_hint')}{' '}
                    <a
                        href={paths.rentalProducts}
                        target={'_blank'}
                        rel={'noopener noreferrer'}
                        className={'text-primary hover:underline'}
                    >
                        {t('station_create.link.create_in_services_link')}
                    </a>
                </p>
            </div>
        </AssetDetailSection>
    );
};

export default LinkProductStep;
